import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { api, post } from "../../lib/api";
import { useCan } from "../../lib/session";
import { Card, Empty, ErrorNote, Field, Modal, PageHeader } from "../../components/ui";

export interface Org {
  companies: Array<{ id: string; code: string; name: string; kind: string; parentId: string | null; active: boolean }>;
  businessUnits: Array<{ id: string; companyId: string; code: string; name: string }>;
  departments: Array<{ id: string; companyId: string; businessUnitId: string | null; code: string; name: string }>;
  sites: Array<{ id: string; companyId: string; businessUnitId: string | null; code: string; name: string; kind: string }>;
  teams: Array<{ id: string; key: string; name: string; kind: string; companyId: string; businessUnitId: string | null; departmentId: string | null }>;
}

export function useOrg() {
  return useQuery({ queryKey: ["org"], queryFn: () => api<Org>("/api/directory/tree") });
}

type Kind = "business-units" | "departments" | "sites" | "teams";
const KIND_LABEL: Record<Kind, string> = { "business-units": "Business unit", departments: "Department", sites: "Site", teams: "Team / crew" };

function AddDialog({ kind, companyId, org, onClose }: { kind: Kind; companyId: string; org: Org; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});
  const set = (k: string) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const m = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { companyId, name: form.name };
      if (kind === "teams") body.key = form.code;
      else body.code = form.code;
      if (form.businessUnitId && kind !== "business-units") body.businessUnitId = form.businessUnitId;
      if (kind === "sites" && form.kind) body.kind = form.kind;
      if (kind === "sites" && form.address) body.address = form.address;
      if (kind === "teams" && form.kind) body.kind = form.kind;
      return post(`/api/directory/${kind}`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org"] });
      onClose();
    },
  });
  const bus = org.businessUnits.filter((b) => b.companyId === companyId);
  return (
    <Modal
      title={`Add ${KIND_LABEL[kind].toLowerCase()}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={m.isPending || !form.name || !form.code} onClick={() => m.mutate()}>Save</button>
        </>
      }
    >
      <ErrorNote error={m.error} />
      <Field label="Name"><input className="input" value={form.name ?? ""} onChange={set("name")} /></Field>
      <Field label={kind === "teams" ? "Key" : "Code"} hint={kind === "teams" ? "lowercase, e.g. mythos-bar" : "short code, e.g. BAR"}>
        <input className="input" value={form.code ?? ""} onChange={set("code")} />
      </Field>
      {kind !== "business-units" && bus.length > 0 && (
        <Field label="Business unit">
          <select className="input" value={form.businessUnitId ?? ""} onChange={set("businessUnitId")}>
            <option value="">—</option>
            {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
      )}
      {kind === "sites" && (
        <>
          <Field label="Kind">
            <select className="input" value={form.kind ?? "site"} onChange={set("kind")}>
              {["site", "office", "venue", "property", "project_site", "warehouse"].map((k) => <option key={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Address"><input className="input" value={form.address ?? ""} onChange={set("address")} /></Field>
        </>
      )}
      {kind === "teams" && (
        <Field label="Kind">
          <select className="input" value={form.kind ?? "team"} onChange={set("kind")}>
            {["team", "crew", "committee"].map((k) => <option key={k}>{k}</option>)}
          </select>
        </Field>
      )}
    </Modal>
  );
}

export function OrganisationPage() {
  const org = useOrg();
  const can = useCan();
  const [adding, setAdding] = useState<{ kind: Kind; companyId: string } | null>(null);
  if (org.isLoading) return <Empty>Loading…</Empty>;
  if (org.error || !org.data) return <ErrorNote error={org.error} />;
  const d = org.data;
  const manage = can("directory.org.manage");
  return (
    <div>
      <PageHeader title="Organisation" subtitle="Companies, business units, departments, sites and teams." />
      <div className="space-y-4">
        {d.companies
          .slice()
          .sort((a, b) => (a.kind === "group" ? -1 : b.kind === "group" ? 1 : a.name.localeCompare(b.name)))
          .map((c) => {
            const sections: Array<[Kind, Array<{ id: string; name: string; code?: string; key?: string; kind?: string }>]> = [
              ["business-units", d.businessUnits.filter((x) => x.companyId === c.id)],
              ["departments", d.departments.filter((x) => x.companyId === c.id)],
              ["sites", d.sites.filter((x) => x.companyId === c.id)],
              ["teams", d.teams.filter((x) => x.companyId === c.id)],
            ];
            return (
              <Card key={c.id} title={<span>{c.name} <span className="chip ml-2">{c.code}</span>{c.kind === "group" && <span className="chip ml-1">group</span>}</span>}>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {sections.map(([kind, items]) => (
                    <div key={kind}>
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="muted text-xs font-semibold uppercase tracking-wide">{KIND_LABEL[kind]}s</h3>
                        {manage && (
                          <button className="btn !min-h-0 !px-2 !py-0.5 text-xs" onClick={() => setAdding({ kind, companyId: c.id })} aria-label={`Add ${KIND_LABEL[kind]}`}>
                            <Plus size={12} /> Add
                          </button>
                        )}
                      </div>
                      {items.length === 0 ? (
                        <p className="muted text-xs">None yet</p>
                      ) : (
                        <ul className="space-y-1 text-sm">
                          {items.map((i) => (
                            <li key={i.id} className="flex items-center justify-between gap-2">
                              <span className="truncate">{i.name}</span>
                              <span className="muted text-xs">{i.code ?? i.key}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
      </div>
      {adding && <AddDialog kind={adding.kind} companyId={adding.companyId} org={d} onClose={() => setAdding(null)} />}
    </div>
  );
}
